import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { TrendingUp, Facebook, Phone, Mail, FileText, RefreshCw, Download, AlertTriangle } from "lucide-react";

interface Report {
  window_days: number;
  generated_at: string;
  totals: { events: number; sessions: number; facebook_attributed_sessions: number };
  events_by_name: Record<string, number>;
  sessions_by_source: Record<string, number>;
  facebook: {
    click_placements: Record<string, number>;
    attributed_conversions: { phone_call_click: number; email_click: number; quote_form_submit: number };
    clicks_by_day?: Record<string, number>;
    day_to_day_targets?: Record<string, number>;
    day_to_day_by_day?: Record<string, number>;
  };
}

function AdminAnalyticsPage() {
  const [token, setToken] = useState("");
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [spikeThreshold, setSpikeThreshold] = useState(10);

  // Persist token + threshold across reloads (admin convenience only — not a security boundary).
  useEffect(() => {
    const saved = localStorage.getItem("tms.admin.report.token") || "";
    if (saved) setToken(saved);
    const t = Number(localStorage.getItem("tms.admin.report.spikeThreshold"));
    if (Number.isFinite(t) && t > 0) setSpikeThreshold(t);
  }, []);

  useEffect(() => {
    localStorage.setItem("tms.admin.report.spikeThreshold", String(spikeThreshold));
  }, [spikeThreshold]);

  async function load(t: string, d: number) {
    if (!t) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/public/analytics-report?token=${encodeURIComponent(t)}&days=${d}`);
      if (!res.ok) {
        setError(res.status === 401 ? "Invalid token" : `Error ${res.status}`);
        setReport(null);
      } else {
        setReport((await res.json()) as Report);
        localStorage.setItem("tms.admin.report.token", t);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="flex-1 bg-cream py-12">
        <div className="mx-auto max-w-6xl px-4 lg:px-8">
          <div className="font-display text-sm tracking-[0.3em] text-rust">★ INTERNAL ★</div>
          <h1 className="mt-2 font-display text-4xl font-bold text-navy-deep md:text-5xl">
            Traffic & Conversion Report
          </h1>
          <p className="mt-3 max-w-2xl text-navy-deep/70">
            Live counts for Facebook clicks, phone taps, email clicks, and form submits — plus
            conversions attributed to visitors who arrived from Facebook.
          </p>

          {/* Controls */}
          <form
            className="mt-8 flex flex-wrap items-end gap-3 rounded-sm border border-navy-deep/15 bg-white p-4"
            onSubmit={(e) => {
              e.preventDefault();
              void load(token, days);
            }}
          >
            <label className="flex flex-col text-xs font-semibold uppercase tracking-wider text-navy-deep/70">
              Report Token
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="ANALYTICS_REPORT_TOKEN"
                className="mt-1 w-72 border border-navy-deep/20 bg-white px-3 py-2 font-mono text-sm text-navy-deep"
              />
            </label>
            <label className="flex flex-col text-xs font-semibold uppercase tracking-wider text-navy-deep/70">
              Window
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="mt-1 border border-navy-deep/20 bg-white px-3 py-2 text-sm text-navy-deep"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </label>
            <label className="flex flex-col text-xs font-semibold uppercase tracking-wider text-navy-deep/70">
              Spike Alert ≥
              <input
                type="number"
                min={1}
                value={spikeThreshold}
                onChange={(e) => setSpikeThreshold(Math.max(1, Number(e.target.value) || 1))}
                title="Trigger an alert when today's 'See our Day to Day work' header clicks meet or exceed this number."
                className="mt-1 w-24 border border-navy-deep/20 bg-white px-3 py-2 text-sm text-navy-deep"
              />
            </label>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 bg-navy-deep px-5 py-2.5 font-display text-sm font-bold uppercase tracking-wider text-white transition hover:bg-navy disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Loading…" : "Load Report"}
            </button>
          </form>

          {error && (
            <div className="mt-4 border-l-4 border-red bg-red/10 p-4 text-sm text-navy-deep">
              {error}
            </div>
          )}

          {report && (
            <div className="mt-8 space-y-8">
              <SpikeAlert report={report} threshold={spikeThreshold} />

              {/* Top-line stats */}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <Stat label="Total Events" value={report.totals.events} icon={TrendingUp} />
                <Stat label="Unique Sessions" value={report.totals.sessions} icon={TrendingUp} />
                <Stat
                  label="From Facebook"
                  value={report.totals.facebook_attributed_sessions}
                  icon={Facebook}
                  accent
                />
                <Stat
                  label={`"See our Day to Day Work" Clicks`}
                  value={
                    (report.facebook.click_placements["header_desktop"] || 0) +
                    (report.facebook.click_placements["header_mobile"] || 0)
                  }
                  icon={Facebook}
                  accent
                />
              </div>

              {/* Facebook conversions */}
              <Card title="Facebook → Conversions">
                <div className="grid gap-4 sm:grid-cols-3">
                  <Conv
                    icon={Phone}
                    label="Phone Calls"
                    value={report.facebook.attributed_conversions.phone_call_click}
                  />
                  <Conv
                    icon={Mail}
                    label="Email Clicks"
                    value={report.facebook.attributed_conversions.email_click}
                  />
                  <Conv
                    icon={FileText}
                    label="Quote Submits"
                    value={report.facebook.attributed_conversions.quote_form_submit}
                  />
                </div>
              </Card>

              <div className="grid gap-6 md:grid-cols-2">
                <Card title="Events by Name">
                  <Table rows={report.events_by_name} />
                </Card>
                <Card title="Sessions by Source">
                  <Table rows={report.sessions_by_source} />
                </Card>
              </div>

              <Card
                title="Facebook Click Placements"
                action={
                  <ExportCsvButton
                    filename={`facebook-clicks-placements-${report.window_days}d.csv`}
                    headers={["placement", "clicks"]}
                    rows={Object.entries(report.facebook.click_placements).sort((a, b) => b[1] - a[1])}
                  />
                }
              >
                <Table rows={report.facebook.click_placements} />
              </Card>

              {report.facebook.clicks_by_day && (
                <Card
                  title="Facebook Clicks — Daily"
                  action={
                    <ExportCsvButton
                      filename={`facebook-clicks-daily-${report.window_days}d.csv`}
                      headers={["date", "clicks"]}
                      rows={Object.entries(report.facebook.clicks_by_day).sort(([a], [b]) => a.localeCompare(b))}
                    />
                  }
                >
                  <DailyChart data={report.facebook.clicks_by_day} />
                </Card>
              )}

              {report.facebook.day_to_day_targets && (
                <Card
                  title='"See our Day to Day Work" — URL Variants'
                  action={
                    <ExportCsvButton
                      filename={`facebook-day-to-day-targets-${report.window_days}d.csv`}
                      headers={["target_url", "clicks"]}
                      rows={Object.entries(report.facebook.day_to_day_targets).sort((a, b) => b[1] - a[1])}
                    />
                  }
                >
                  {Object.keys(report.facebook.day_to_day_targets).length === 0 ? (
                    <div className="text-sm text-navy-deep/70">No clicks yet.</div>
                  ) : (
                    <ul className="space-y-2">
                      {Object.entries(report.facebook.day_to_day_targets)
                        .sort((a, b) => b[1] - a[1])
                        .map(([url, count]) => (
                          <li
                            key={url}
                            className="flex items-start justify-between gap-3 border border-navy-deep/10 bg-cream p-3"
                          >
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="break-all font-mono text-xs text-navy-deep underline decoration-navy-deep/30 underline-offset-2 hover:text-rust"
                              title={url}
                            >
                              {url}
                            </a>
                            <span className="shrink-0 font-display text-sm font-bold text-navy-deep">
                              {count}
                            </span>
                          </li>
                        ))}
                    </ul>
                  )}
                </Card>
              )}

              <p className="text-xs text-navy-deep/70">
                Generated {new Date(report.generated_at).toLocaleString()}.
              </p>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  accent,
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  accent?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-4 border-l-4 ${
        accent ? "border-[#1877F2] bg-white" : "border-navy-deep bg-white"
      } p-5 shadow-card`}
    >
      <Icon className={`h-8 w-8 ${accent ? "text-[#1877F2]" : "text-navy-deep"}`} />
      <div>
        <div className="font-display text-3xl font-bold text-navy-deep">{value.toLocaleString()}</div>
        <div className="text-xs uppercase tracking-wider text-navy-deep/60">{label}</div>
      </div>
    </div>
  );
}

function Conv({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center gap-3 border border-navy-deep/15 bg-cream p-4">
      <Icon className="h-6 w-6 text-rust" />
      <div>
        <div className="font-display text-2xl font-bold text-navy-deep">{value}</div>
        <div className="text-xs uppercase tracking-wider text-navy-deep/60">{label}</div>
      </div>
    </div>
  );
}

function Card({ title, children, action }: { title: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <div className="bg-white p-6 shadow-card">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display text-lg font-bold uppercase tracking-wider text-navy-deep">{title}</h2>
        {action}
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function ExportCsvButton({
  filename,
  headers,
  rows,
}: {
  filename: string;
  headers: [string, string];
  rows: Array<[string, number]>;
}) {
  const handleDownload = () => {
    const escape = (v: string | number) => {
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.map(escape).join(",")];
    for (const [k, v] of rows) lines.push(`${escape(k)},${escape(v)}`);
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={rows.length === 0}
      className="inline-flex items-center gap-2 border border-navy-deep/20 bg-cream px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wider text-navy-deep transition hover:bg-navy-deep hover:text-white disabled:opacity-40"
    >
      <Download className="h-3.5 w-3.5" /> CSV
    </button>
  );
}

function Table({ rows }: { rows: Record<string, number> }) {
  const entries = Object.entries(rows).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <div className="text-sm text-navy-deep/70">No data.</div>;
  const max = entries[0][1];
  return (
    <ul className="space-y-2">
      {entries.map(([k, v]) => (
        <li key={k} className="flex items-center gap-3">
          <span className="w-44 truncate text-sm font-mono text-navy-deep">{k}</span>
          <span className="relative h-5 flex-1 bg-cream">
            <span
              className="absolute inset-y-0 left-0 bg-rust/70"
              style={{ width: `${Math.max(4, (v / max) * 100)}%` }}
            />
          </span>
          <span className="w-12 text-right font-display text-sm font-bold text-navy-deep">{v}</span>
        </li>
      ))}
    </ul>
  );
}

function SpikeAlert({ report, threshold }: { report: Report; threshold: number }) {
  const byDay = report.facebook.day_to_day_by_day;
  if (!byDay) return null;
  const days = Object.keys(byDay).sort();
  if (days.length === 0) return null;

  const today = days[days.length - 1];
  const todayCount = byDay[today] ?? 0;
  const prior = days.slice(0, -1).map((d) => byDay[d] ?? 0);
  const avg = prior.length > 0 ? prior.reduce((s, v) => s + v, 0) / prior.length : 0;
  const peakPrior = prior.length > 0 ? Math.max(...prior) : 0;

  if (todayCount < threshold) return null;

  const vsAvg = avg > 0 ? `${(todayCount / avg).toFixed(1)}× the ${report.window_days}-day average (${avg.toFixed(1)}/day)` : "no prior baseline";
  const vsPeak = peakPrior > 0 ? `, prior peak ${peakPrior}` : "";

  return (
    <div
      role="alert"
      className="flex items-start gap-3 border-l-4 border-red bg-red/10 p-4 text-sm text-navy-deep"
    >
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red" />
      <div>
        <div className="font-display text-base font-bold uppercase tracking-wider text-navy-deep">
          Day-to-Day Click Spike
        </div>
        <div className="mt-1">
          <span className="font-bold">{todayCount}</span> "See our Day to Day work" header click
          {todayCount === 1 ? "" : "s"} today ({today}) — at or above your threshold of{" "}
          <span className="font-bold">{threshold}</span>.
        </div>
        <div className="mt-1 text-navy-deep/70">
          {vsAvg}
          {vsPeak}.
        </div>
      </div>
    </div>
  );
}

function DailyChart({ data }: { data: Record<string, number> }) {
  const entries = Object.entries(data).sort(([a], [b]) => a.localeCompare(b));
  if (entries.length === 0) return <div className="text-sm text-navy-deep/70">No data.</div>;

  const W = 800;
  const H = 220;
  const padL = 36;
  const padR = 12;
  const padT = 12;
  const padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const max = Math.max(1, ...entries.map(([, v]) => v));
  const barW = innerW / entries.length;
  const total = entries.reduce((s, [, v]) => s + v, 0);
  const yTicks = 4;

  return (
    <div>
      <div className="mb-3 text-xs uppercase tracking-wider text-navy-deep/60">
        {entries.length} days · {total.toLocaleString()} total clicks · peak {max}
      </div>
      <div className="w-full overflow-x-auto">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="h-56 w-full min-w-[600px]"
          role="img"
          aria-label="Daily Facebook clicks"
        >
          {/* Y gridlines + labels */}
          {Array.from({ length: yTicks + 1 }).map((_, i) => {
            const y = padT + (innerH * i) / yTicks;
            const val = Math.round(max - (max * i) / yTicks);
            return (
              <g key={i}>
                <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="currentColor" className="text-navy-deep/10" />
                <text x={padL - 6} y={y + 3} textAnchor="end" className="fill-navy-deep/60" fontSize={10}>
                  {val}
                </text>
              </g>
            );
          })}

          {/* Bars */}
          {entries.map(([day, v], i) => {
            const h = (v / max) * innerH;
            const x = padL + i * barW + barW * 0.15;
            const w = barW * 0.7;
            const y = padT + innerH - h;
            return (
              <g key={day}>
                <rect x={x} y={y} width={w} height={h} className="fill-[#1877F2]" rx={1}>
                  <title>{`${day}: ${v}`}</title>
                </rect>
              </g>
            );
          })}

          {/* X labels — show ~6 evenly spaced */}
          {entries.map(([day], i) => {
            const step = Math.max(1, Math.floor(entries.length / 6));
            if (i % step !== 0 && i !== entries.length - 1) return null;
            const x = padL + i * barW + barW / 2;
            return (
              <text
                key={`x-${day}`}
                x={x}
                y={H - 8}
                textAnchor="middle"
                className="fill-navy-deep/60"
                fontSize={10}
              >
                {day.slice(5)}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/admin/analytics")({
  head: () => ({
    meta: [
      { title: "Internal — Traffic & Conversion Report" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminAnalyticsPage,
});
